// Design System Card Component
import React from 'react';
import { Card as MuiCard, CardProps as MuiCardProps, CardContent, CardActions } from '@mui/material';
import { styled } from '@mui/material/styles';
import { DESIGN_TOKENS, SPACING_TOKENS } from '../tokens';

type CardVariant = 'default' | 'elevated' | 'outlined' | 'interactive';
type CardPadding = 'none' | 'small' | 'medium' | 'large';

interface CardProps extends Omit<MuiCardProps, 'variant'> {
  variant?: CardVariant;
  padding?: CardPadding;
  interactive?: boolean;
  children?: React.ReactNode;
}

// Styled card with design system tokens
const StyledCard = styled(MuiCard, {
  shouldForwardProp: (prop) => !['variant', 'padding', 'interactive'].includes(prop as string),
})<CardProps>(({ variant = 'default', padding = 'medium', interactive = false }) => {
  const paddingStyles = {
    none: { padding: 0 },
    small: { padding: SPACING_TOKENS.semantic.sm },
    medium: { padding: SPACING_TOKENS.semantic.md },
    large: { padding: SPACING_TOKENS.semantic.lg },
  };

  const variantStyles = {
    default: {
      backgroundColor: DESIGN_TOKENS.colors.background.paper,
      border: `1px solid ${DESIGN_TOKENS.colors.neutral[100]}`,
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)',
    },
    elevated: {
      backgroundColor: DESIGN_TOKENS.colors.background.paper,
      border: 'none',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    },
    outlined: {
      backgroundColor: DESIGN_TOKENS.colors.background.paper,
      border: `2px solid ${DESIGN_TOKENS.colors.neutral[200]}`,
      boxShadow: 'none',
    },
    interactive: {
      backgroundColor: DESIGN_TOKENS.colors.background.paper,
      border: `1px solid ${DESIGN_TOKENS.colors.neutral[100]}`,
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
        borderColor: DESIGN_TOKENS.colors.primary[200],
      },
      '&:active': {
        transform: 'translateY(0)',
      },
    },
  };

  const baseStyles = {
    borderRadius: 12,
    overflow: 'hidden',
    position: 'relative' as const,
    ...paddingStyles[padding as keyof typeof paddingStyles],
    ...(variantStyles[variant as keyof typeof variantStyles] || variantStyles.default),
  };

  if (interactive) {
    return {
      ...baseStyles,
      ...variantStyles.interactive,
    };
  }

  return baseStyles;
});

// Card component with forwardRef
export const Card = React.forwardRef<HTMLDivElement, CardProps>(({
  children,
  variant = 'default',
  padding = 'medium',
  interactive = false,
  onClick,
  ...props
}, ref) => {
  const isInteractive = interactive || !!onClick;
  
  return (
    <StyledCard
      ref={ref}
      variant={variant as any}
      padding={padding}
      interactive={isInteractive}
      onClick={onClick}
      {...props}
    >
      {children}
    </StyledCard>
  );
});

Card.displayName = 'Card';

// Card Content with consistent padding
export const CardBody = styled(CardContent)({
  padding: SPACING_TOKENS.semantic.md,
  '&:last-child': {
    paddingBottom: SPACING_TOKENS.semantic.md,
  },
});

// Card Actions with consistent styling
export const CardFooter = styled(CardActions)({
  padding: SPACING_TOKENS.semantic.md,
  paddingTop: SPACING_TOKENS.semantic.sm,
  justifyContent: 'flex-end',
  gap: SPACING_TOKENS.semantic.sm,
});

export default Card;